import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { platformColor, platformKeys, platformLogo } from "../api/platforms";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { combinedCoverageMapV1StoresMapGet } from "../api/generated/qcsControlPlane";
import type { MapIdentityKind, MapStore } from "../api/generated/model";
import { MapLocationKind } from "../api/generated/model";
import {
  type LatLon,
  type Platform,
  type StoreCell,
  platformLabel,
} from "../api/labels";
import { canonTier, tierColor, tierLabel, TIER_ORDER } from "./tiers";
import { donutClusterIcon } from "./mapCluster";
import { IconCollapseCorners, IconExpandCorners } from "./icons";

// All-platforms store coverage map. Replaces the old server-rendered Folium
// iframe with a real Leaflet component: per-platform logo markers (clustered),
// click a store to pin its serviceable-area hull + the probe points where it was
// found, multi-select to compare several at once, and an expand-to-fullscreen
// toggle. Data comes from /v1/stores/map already decoded to [lat, lon] pairs.

const INDIA_BOUNDS: L.LatLngBoundsExpression = [
  [6.5, 68.0],
  [37.5, 97.5],
];
const LOGO = platformLogo;

// A store's logo marker (round), ringed by its delivery-tier colour so the store's
// class (Blinkit express/unicorn/longtail/super_longtail, or Instamart/Zepto primary/
// secondary) reads at a glance without selecting it. The inner .qcs-mk div is what we
// toggle .qcs-mk-sel on when selected.
function storeIcon(platform: string, ring: string, inferred: boolean): L.DivIcon {
  return L.divIcon({
    className: "",
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    html:
      `<div class="qcs-mk qcs-mk-tier${inferred ? " qcs-mk-inferred" : ""}" ` +
      `style="background-image:url(${LOGO(platform)});` +
      `border-color:${ring}"></div>`,
  });
}

// A store's dominant cell tier (the delivery class / role it served at the most probe
// points) -> the colour for its marker ring and the always-on tier legend. "" when the
// store has no tagged cells (older sweep / boundary-only) -> the marker falls back to its
// platform colour.
function dominantTier(cells: StoreCell[]): string {
  const counts = new Map<string, number>();
  for (const [, , tier] of cells) {
    const t = canonTier(tier);
    if (t) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  let best = "";
  let max = 0;
  for (const [t, n] of counts) {
    if (n > max) {
      best = t;
      max = n;
    }
  }
  return best;
}

const tierRank = (t: string) => {
  const i = TIER_ORDER.indexOf(t);
  return i === -1 ? TIER_ORDER.length : i;
};

// All platforms share ONE cluster group, so a cluster describes a place rather than a
// place-per-layer. With three layers on, per-platform groups clustered independently and
// stacked three near-identical bubbles over every city -- the mess this replaces.
//
// One platform underneath -> its logo + a count badge, the familiar single-layer look.
// Several -> a donut of the platforms' registry brand colours (mapCluster.ts), so the mix
// reads without expanding. Colours come from GET /v1/platforms; none are invented here.
type PlatformMarker = L.Marker & { _qcsPlatform?: string };

function clusterIcon(cluster: L.MarkerCluster): L.DivIcon {
  const n = cluster.getChildCount();
  const counts = new Map<string, number>();
  for (const c of cluster.getAllChildMarkers()) {
    const p = (c as PlatformMarker)._qcsPlatform ?? "";
    counts.set(p, (counts.get(p) ?? 0) + 1);
  }

  if (counts.size > 1) {
    // platformKeys() order keeps the ring stable as clusters merge and split
    const slices = platformKeys()
      .filter((p) => counts.has(p))
      .map((p) => ({ color: platformColor(p), label: platformLabel(p), n: counts.get(p) ?? 0 }));
    return donutClusterIcon(slices, n, platformColor(""));
  }

  const platform = [...counts.keys()][0] ?? "";
  const color = platformColor(platform);
  const s = n < 10 ? 34 : n < 100 ? 40 : 46;
  return L.divIcon({
    className: "",
    iconSize: L.point(s, s),
    iconAnchor: L.point(s / 2, s / 2),
    html:
      `<div title="${n} ${platformLabel(platform)} stores" ` +
      `style="position:relative;width:${s}px;height:${s}px;border:2px solid #fff;` +
      `border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.5);` +
      `background:#fff url(${LOGO(platform)}) center/cover no-repeat">` +
      `<span style="position:absolute;right:-3px;bottom:-3px;min-width:17px;height:17px;` +
      `padding:0 4px;box-sizing:border-box;background:${color};color:#fff;border:2px solid #fff;` +
      `border-radius:9px;font:800 10px/13px Archivo,system-ui,sans-serif;text-align:center">${n}</span>` +
      `</div>`,
  });
}

const storeKey = (platform: string, id: string) => `${platform}:${id}`;

// Click-anywhere popup: shows the clicked point's lat/lon to 6 dp (~0.1 m) with a
// one-click copy of the "lat, lon" pair: handy for grabbing a probe coordinate
// straight off the map. Built via DOM so the copy button can carry a click handler.
function coordPopup(lat: number, lon: number): HTMLElement {
  const pair = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
  const el = document.createElement("div");
  el.className = "scov__pop scov__coord";

  const v = document.createElement("div");
  v.className = "scov__coord-val mono";
  v.textContent = pair;
  el.appendChild(v);

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "scov__coord-copy";
  btn.textContent = "Copy";
  btn.onclick = () => {
    navigator.clipboard?.writeText(pair).then(
      () => {
        btn.textContent = "Copied ✓";
        setTimeout(() => (btn.textContent = "Copy"), 1200);
      },
      () => (btn.textContent = "Copy failed"),
    );
  };
  el.appendChild(btn);

  return el;
}

// What a marker's id identifies, in the operator's words. Keyed by the generated union,
// so a new identity kind on the API is a type error here rather than a raw snake_case
// token rendered into the popup.
const IDENTITY_LABEL: Record<MapIdentityKind, string> = {
  store: "Store identity",
  observed_fulfilment_center: "Fulfilment-center identity",
};

// A marker sitting at a footprint centre we derived, not a coordinate the platform
// handed us. Drives the ringed marker, the tooltip suffix and the popup line, so the
// three can never disagree about which stores are inferred.
function isInferredCenter(st: MapStore): boolean {
  return st.location_kind === MapLocationKind.inferred_from_serviceable_footprint;
}

// Click popup for a store marker: id + ETA and a link that opens the store's full
// detail page in a new tab. Built via DOM (not an HTML string) so an odd store id
// can't break out of the markup.
function storePopup(platform: string, st: MapStore): HTMLElement {
  const el = document.createElement("div");
  el.className = "scov__pop";

  const idLine = document.createElement("div");
  idLine.className = "scov__pop-id";
  idLine.textContent = st.store_id;
  if (st.eta_minutes != null) {
    const e = document.createElement("span");
    e.className = "scov__pop-eta";
    e.textContent = ` · ${st.eta_minutes}m`;
    idLine.appendChild(e);
  }
  el.appendChild(idLine);

  const inferred = isInferredCenter(st);
  const provenance = document.createElement("div");
  provenance.className = `scov__pop-provenance${inferred ? " inferred" : ""}`;
  provenance.textContent = inferred
    ? "Footprint center · not an API store coordinate"
    : "Store coordinate reported by platform";
  el.appendChild(provenance);

  if (st.identity_kind && st.identity_kind !== "store") {
    const identity = document.createElement("div");
    identity.className = "scov__pop-meta";
    identity.textContent = IDENTITY_LABEL[st.identity_kind];
    el.appendChild(identity);
  }

  const a = document.createElement("a");
  a.href = `/stores/${platform}/${encodeURIComponent(st.store_id)}`;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.className = "scov__pop-link";
  a.textContent = "Open store page ↗";
  el.appendChild(a);

  return el;
}

// Distinct, map-legible hues handed out per selected store so overlapping
// serviceable areas stay tellable apart (independent of the store's platform).
const SELECTION_COLORS = [
  "#e6194b", "#4363d8", "#3cb44b", "#f58231", "#911eb4",
  "#008080", "#9a6324", "#f032e6", "#808000", "#000075",
];

// Darken a #rrggbb toward black by fraction `f`: used to make probe points read
// as darker, denser dots than their (lighter) serviceable-area fill.
function darken(hex: string, f: number): string {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  const r = Math.round(((n >> 16) & 255) * (1 - f));
  const g = Math.round(((n >> 8) & 255) * (1 - f));
  const b = Math.round((n & 255) * (1 - f));
  return `rgb(${r},${g},${b})`;
}

// The role tiers Instamart (and Zepto) assign per location: a store is PRIMARY at the
// points it's the default/fast fulfiller and SECONDARY where it only backs up another
// store. A single store can be both: primary over its core area, secondary on the
// fringe, so a selected store's probe points are split by these and shaded apart.
const ROLE_TIERS = ["primary", "secondary"] as const;
const isRole = (t: string): boolean => t === "primary" || t === "secondary";

// The sweep's finest probe spacing, in degrees (mirrors merchant_dense_step / the
// backend's _DENSE_STEP). Its hex lattice is equilateral in degree space, so the cell a
// probe speaks for is a regular hexagon with corners at circumradius step/sqrt(3), spaced
// 60 deg apart from 30 deg: the [dlat, dlon] offsets below. Keep in step with
// qcs.core.geo.cell_hull, whose polygons these must match.
const DENSE_STEP = 0.005;
const CELL_CORNERS: LatLon[] = Array.from({ length: 6 }, (_, k) => {
  const a = Math.PI / 6 + (k * Math.PI) / 3;
  const r = DENSE_STEP / Math.sqrt(3);
  return [r * Math.sin(a), r * Math.cos(a)];
});

// Andrew's monotone-chain convex hull over [lat, lon] points -> the outer ring (>=3
// points), used to draw a tidy region polygon around one tier's probe points when the
// backend ships per-store cells but only a single combined serviceable hull.
function convexHull(pts: LatLon[]): LatLon[] {
  const uniq = [...new Map(pts.map((p) => [`${p[0]},${p[1]}`, p])).values()];
  if (uniq.length < 3) return [];
  const p = uniq.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: LatLon, a: LatLon, b: LatLon) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: LatLon[] = [];
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0)
      lower.pop();
    lower.push(q);
  }
  const upper: LatLon[] = [];
  for (let i = p.length - 1; i >= 0; i--) {
    const q = p[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0)
      upper.pop();
    upper.push(q);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

// Convex hull of probe points grown to cover their whole cells, not just the points at
// their centres: the browser-side twin of qcs.core.geo.cell_hull, and exact for the same
// reason: replacing each point with its hexagon and re-hulling is the Minkowski sum of two
// convex shapes, so no arc sampling or pad radius is involved. Without it a ring stops half
// a cell short on every edge and neighbouring stores show a seam that isn't really a gap.
// It also gives 1- and 2-point inputs a real polygon, where bare convexHull gives none.
function cellHull(pts: LatLon[]): LatLon[] {
  const grown: LatLon[] = [];
  for (const [lat, lon] of pts)
    for (const [dlat, dlon] of CELL_CORNERS) grown.push([lat + dlat, lon + dlon]);
  return convexHull(grown);
}

// The serviceable-area ring to draw for a store: its backend hull when present,
// otherwise the cell hull of its probe points. Used both by a single selection and
// by the "show all serviceable areas" bulk overlay.
function storeHull(st: MapStore): LatLon[] {
  if (st.boundary && st.boundary.length >= 3) return st.boundary as LatLon[];
  return cellHull((st.cells as StoreCell[]).map(([lat, lon]) => [lat, lon] as LatLon));
}

// Everything we draw for one selected store, so we can remove it on deselect. Tier lives
// on the marker ring (always on, see dominantTier), so a selection only needs its drawn
// layers + the per-store colour its dots/hull are painted in.
type Selection = { layers: L.Layer[]; iconEl: HTMLElement | null; color: string };

export function StoresCoverageMap({
  activePlatform,
  filterIds,
  focusStoreId,
}: {
  activePlatform: Platform;
  // when set, the active platform's markers are limited to these store ids (the
  // Stores-table filter, reflected onto the map); null/undefined = show all.
  filterIds?: ReadonlySet<string> | null;
  // deep link from a store page: zoom to this store and pin it, once per id
  focusStoreId?: string | null;
}) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  // one group for every platform (see clusterIcon), plus each platform's markers kept
  // aside so a layer toggle adds/removes just its own from the shared group
  const clusterRef = useRef<L.MarkerClusterGroup | null>(null);
  const platformMarkersRef = useRef<Record<string, L.Marker[]>>({});
  const areasLayerRef = useRef<L.LayerGroup | null>(null);
  const selLayerRef = useRef<L.LayerGroup | null>(null);
  const selectedRef = useRef<Map<string, Selection>>(new Map());
  // every built marker, so the focus deep link can find and pin one without a re-render
  const markersRef = useRef<Map<string, { marker: L.Marker; store: MapStore; platform: string }>>(
    new Map(),
  );
  // guards the focus effect so it fires once per focus id, not on every 30s refetch
  const lastFocusRef = useRef<string | null>(null);
  const colorUseRef = useRef<Map<string, number>>(new Map(SELECTION_COLORS.map((c) => [c, 0])));

  const [fullscreen, setFullscreen] = useState(false);
  // when on, overlay every visible store's serviceable-area hull at once (platform-
  // coloured, translucent) without needing to click each store one by one.
  const [showAreas, setShowAreas] = useState(false);
  // The page is scoped to one platform, so the map starts showing only that
  // platform; the others are off until the operator enables them, and switching
  // the page's platform refocuses the map on it (see the reset effect below).
  const [visible, setVisible] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(platformKeys().map((p) => [p, p === activePlatform])),
  );
  const [selectedCount, setSelectedCount] = useState(0);

  const map = useQuery({
    queryKey: ["stores-map"],
    queryFn: () => combinedCoverageMapV1StoresMapGet(),
    refetchInterval: 30000,
  });

  // -- create the map once --------------------------------------------------
  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const selected = selectedRef.current; // captured for the cleanup, see below
    const m = L.map(elRef.current, { attributionControl: false, preferCanvas: true }).fitBounds(
      INDIA_BOUNDS,
    );
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(m);
    // areas under selection so a pinned store's hull stays legible on top of the bulk overlay
    areasLayerRef.current = L.layerGroup().addTo(m);
    selLayerRef.current = L.layerGroup().addTo(m);
    // click empty map (markers stop their own clicks) -> popup with that point's
    // lat/lon + copy button.
    m.on("click", (e: L.LeafletMouseEvent) => {
      L.popup({ offset: [0, -2], minWidth: 150, className: "scov__coord-pop" })
        .setLatLng(e.latlng)
        .setContent(coordPopup(e.latlng.lat, e.latlng.lng))
        .openOn(m);
    });
    mapRef.current = m;
    setTimeout(() => m.invalidateSize(), 0);
    return () => {
      m.remove();
      mapRef.current = null;
      clusterRef.current = null;
      platformMarkersRef.current = {};
      // the ref object is captured here, not read at cleanup time: by then
      // `selectedRef.current` may point at a different Map
      selected.clear();
    };
  }, []);

  // Hand out the least-used palette colour so each concurrently-selected store
  // gets a distinct hue; freed on deselect so colours recycle.
  const pickColor = (): string => {
    let best = SELECTION_COLORS[0];
    let min = Infinity;
    for (const c of SELECTION_COLORS) {
      const u = colorUseRef.current.get(c) ?? 0;
      if (u < min) {
        min = u;
        best = c;
      }
    }
    colorUseRef.current.set(best, (colorUseRef.current.get(best) ?? 0) + 1);
    return best;
  };
  const releaseColor = (c: string) =>
    colorUseRef.current.set(c, Math.max(0, (colorUseRef.current.get(c) ?? 0) - 1));

  const clearSelection = () => {
    const sel = selLayerRef.current;
    selectedRef.current.forEach((s) => {
      s.iconEl?.classList.remove("qcs-mk-sel");
      s.layers.forEach((l) => sel?.removeLayer(l));
    });
    selectedRef.current.clear();
    colorUseRef.current.forEach((_, c) => colorUseRef.current.set(c, 0));
    setSelectedCount(0);
    applyFocus(null);
  };

  // Focus mode: hovering one pinned store isolates it, every *other* pinned store's
  // dots + hull fade to a faint context layer (12% of their base opacity) and all other
  // markers dim (CSS), so overlapping coverage stops competing. `null` restores parity.
  // Only meaningful with >=2 pinned stores; a no-op otherwise.
  const applyFocus = (focusKey: string | null) => {
    selectedRef.current.forEach((seln, k) => {
      const dim = focusKey != null && k !== focusKey;
      seln.iconEl?.classList.toggle("qcs-mk-focus", focusKey != null && k === focusKey);
      for (const l of seln.layers) {
        const p = l as L.Path & { _base?: { o: number; f: number } };
        if (typeof p.setStyle !== "function" || !p._base) continue;
        p.setStyle({
          opacity: dim ? p._base.o * 0.12 : p._base.o,
          fillOpacity: dim ? p._base.f * 0.12 : p._base.f,
        });
      }
    });
    elRef.current?.classList.toggle("scov--focus", focusKey != null);
  };

  // Switching the page's platform refocuses the map on it AND drops any pinned
  // selections (they belong to whatever platform was being inspected before).
  useEffect(() => {
    clearSelection();
    setVisible(Object.fromEntries(platformKeys().map((p) => [p, p === activePlatform])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlatform]);

  // Toggle a store's serviceable area + probe points + highlight. Each store gets
  // its own colour; probe points are a darker shade of it so they read clearly.
  const toggleStore = (platform: string, st: MapStore, marker: L.Marker) => {
    const sel = selLayerRef.current;
    const map = mapRef.current;
    if (!sel || !map) return;
    const key = storeKey(platform, st.store_id);
    const existing = selectedRef.current.get(key);
    if (existing) {
      existing.iconEl?.classList.remove("qcs-mk-sel", "qcs-mk-focus");
      existing.layers.forEach((l) => sel.removeLayer(l));
      releaseColor(existing.color);
      selectedRef.current.delete(key);
      setSelectedCount(selectedRef.current.size);
      applyFocus(null); // drop any focus so the remaining stores read at full strength
      return;
    }
    const color = pickColor();
    const pointColor = darken(color, 0.4); // darker, denser dots for untiered probe points
    const layers: L.Layer[] = [];

    // Split the store's probe points by the role it served there (primary/secondary);
    // anything untagged or non-role folds into one bucket keyed "". A store that's
    // primary in its core and secondary on the fringe lands in both.
    const byRole = new Map<string, LatLon[]>();
    for (const [lat, lon, tier] of st.cells as StoreCell[]) {
      const t = canonTier(tier);
      const bucket = isRole(t) ? t : "";
      const arr = byRole.get(bucket) ?? [];
      arr.push([lat, lon]);
      byRole.set(bucket, arr);
    }
    const roles = ROLE_TIERS.filter((t) => byRole.has(t));
    const hasRoles = roles.length > 0;

    // serviceable-area outline(s), added first so the probe dots sit on top: for a
    // primary/secondary store, one dashed hull per role (its primary region vs its
    // secondary region as distinct areas); otherwise the single backend hull in the
    // store's selection colour.
    if (hasRoles) {
      for (const t of roles) {
        const ring = cellHull(byRole.get(t) ?? []);
        if (ring.length < 3) continue;
        const c = tierColor(t);
        layers.push(
          L.polygon(ring, {
            color: c,
            weight: 2.5,
            opacity: 0.95,
            fillColor: c,
            fillOpacity: 0.1,
            dashArray: "5 4",
            interactive: false,
          }),
        );
      }
    } else if (st.boundary && st.boundary.length >= 3) {
      layers.push(
        L.polygon(st.boundary as LatLon[], {
          color,
          weight: 2.5,
          opacity: 0.95,
          fillColor: color,
          fillOpacity: 0.12,
          dashArray: "5 4",
          interactive: false,
        }),
      );
    }
    // probe points where this store was found: painted in the store's own selection
    // colour so overlapping stores stay tellable apart (tier lives on the marker ring).
    // Instamart/Zepto role cells still shade by primary/secondary, which the per-store
    // dashed hulls reinforce.
    for (const [lat, lon, tier] of st.cells as StoreCell[]) {
      const t = canonTier(tier);
      layers.push(
        L.circleMarker([lat, lon], {
          radius: 4,
          color: "#fff",
          weight: 1,
          fillColor: isRole(t) ? tierColor(t) : pointColor,
          fillOpacity: 1,
          interactive: false,
        }),
      );
    }
    // high-contrast selection ring at the store point, rimmed in the store's colour
    layers.push(
      L.circleMarker([st.lat, st.lon], {
        radius: 16,
        color,
        weight: 4,
        opacity: 1,
        fill: false,
        interactive: false,
      }),
    );
    // snapshot each layer's base opacities so focus mode can dim then restore them
    for (const l of layers) {
      const p = l as L.Path & { _base?: { o: number; f: number } };
      if (typeof p.setStyle !== "function") continue;
      const o = p.options as L.PathOptions;
      p._base = { o: o.opacity ?? 1, f: o.fillOpacity ?? 0 };
    }
    layers.forEach((l) => l.addTo(sel));
    const iconEl = (marker.getElement()?.querySelector(".qcs-mk") as HTMLElement | null) ?? null;
    iconEl?.classList.add("qcs-mk-sel");
    selectedRef.current.set(key, { layers, iconEl, color });
    setSelectedCount(selectedRef.current.size);
  };

  // -- (re)build platform layers when data changes --------------------------
  useEffect(() => {
    const m = mapRef.current;
    const data = map.data;
    if (!m || !data) return;
    clearSelection();
    // tear down previous layers
    if (clusterRef.current) m.removeLayer(clusterRef.current);
    platformMarkersRef.current = {};
    markersRef.current.clear();

    const cluster = L.markerClusterGroup({
      maxClusterRadius: 44,
      disableClusteringAtZoom: 13,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      chunkedLoading: true,
      iconCreateFunction: clusterIcon,
    });
    clusterRef.current = cluster;

    const order = [...data.platforms].sort(
      (a, b) =>
        platformKeys().indexOf(a.platform) - platformKeys().indexOf(b.platform),
    );

    order.forEach((layer) => {
      const color = platformColor(layer.platform);
      const built: L.Marker[] = [];

      for (const st of layer.stores) {
        // reflect the Stores-table filter: on the active platform, drop markers
        // whose store isn't in the filtered set (other platforms are unaffected).
        if (filterIds && layer.platform === activePlatform && !filterIds.has(st.store_id)) continue;
        const eta = st.eta_minutes != null ? ` · ${st.eta_minutes}m` : "";
        // ring the marker by the store's dominant delivery tier (falls back to the
        // platform colour when the store has no tagged cells); the tooltip names it.
        const tier = dominantTier(st.cells as StoreCell[]);
        const ring = tier ? tierColor(tier) : color;
        const tierTip = tier ? ` · ${tierLabel(tier)}` : "";
        const inferred = isInferredCenter(st);
        const marker = L.marker([st.lat, st.lon], {
          icon: storeIcon(layer.platform, ring, inferred),
        })
          .bindTooltip(`${st.store_id}${eta}${tierTip}${inferred ? " · inferred center" : ""}`, {
            direction: "top",
            offset: [0, -12],
          })
          .bindPopup(storePopup(layer.platform, st), { offset: [0, -10], minWidth: 130 });
        marker.on("click", () => toggleStore(layer.platform, st, marker));
        // hover a pinned store -> isolate it (fade the other pinned stores); leaving
        // restores parity. No-op when the store isn't pinned or only one is pinned.
        const hkey = storeKey(layer.platform, st.store_id);
        marker.on("mouseover", () => {
          if (selectedRef.current.size > 1 && selectedRef.current.has(hkey)) applyFocus(hkey);
        });
        marker.on("mouseout", () => applyFocus(null));
        markersRef.current.set(hkey, { marker, store: st, platform: layer.platform });
        (marker as PlatformMarker)._qcsPlatform = layer.platform;
        built.push(marker);
      }
      platformMarkersRef.current[layer.platform] = built;
    });

    // honour current visibility toggles
    applyVisibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map.data, filterIds, activePlatform]);

  // add/remove each platform's markers per the `visible` toggles. The group itself stays
  // on the map: toggling a layer re-clusters what is left, so the bubbles always describe
  // exactly the platforms currently shown.
  const applyVisibility = () => {
    const m = mapRef.current;
    const cluster = clusterRef.current;
    if (!m || !cluster) return;
    if (!m.hasLayer(cluster)) cluster.addTo(m);
    for (const p of platformKeys()) {
      const markers = platformMarkersRef.current[p];
      if (!markers?.length) continue;
      const on = visible[p];
      const shown = cluster.hasLayer(markers[0]);
      if (on && !shown) cluster.addLayers(markers);
      else if (!on && shown) cluster.removeLayers(markers);
    }
  };
  useEffect(applyVisibility, [visible]);

  // -- "show this store on the map" deep link -------------------------------
  // Force the store's platform layer on, spiderfy it out of its cluster, zoom in, pin it
  // (the pin is what draws its hull + probe points, which is the point of seeing it in
  // context) and open its popup. Guarded by lastFocusRef so the map's 30s refetch does
  // not yank the view back after a manual pan.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !focusStoreId || !map.data) return;
    if (lastFocusRef.current === focusStoreId) return;
    const key = storeKey(activePlatform, focusStoreId);
    const hit = markersRef.current.get(key);
    if (!hit) {
      // Not built: the store is inactive, or the table filter narrowed it away. Fall back
      // to centring on its coordinates if the payload has them. A normal miss, not an error.
      const raw = map.data.platforms
        .find((p) => p.platform === activePlatform)
        ?.stores.find((st) => st.store_id === focusStoreId);
      if (raw) {
        lastFocusRef.current = focusStoreId;
        m.setView([raw.lat, raw.lon], 15);
      }
      return;
    }
    lastFocusRef.current = focusStoreId;
    if (!visible[activePlatform]) {
      setVisible((v) => ({ ...v, [activePlatform]: true }));
      return; // re-runs once the layer is on and the marker is attached to the map
    }
    const cluster = clusterRef.current;
    const reveal = () => {
      m.setView([hit.store.lat, hit.store.lon], 15);
      if (!selectedRef.current.has(key)) toggleStore(hit.platform, hit.store, hit.marker);
      hit.marker.openPopup();
    };
    if (cluster) cluster.zoomToShowLayer(hit.marker, reveal);
    else reveal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusStoreId, map.data, activePlatform, visible]);

  // Bulk serviceable-area overlay: (re)draw every visible+filtered store's hull into its
  // own layer group. Runs only for this reason (never fitBounds), so toggling it or the
  // platform layers leaves the current pan/zoom untouched.
  useEffect(() => {
    const layer = areasLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!showAreas || !map.data) return;
    for (const pl of map.data.platforms) {
      if (!visible[pl.platform]) continue;
      const color = platformColor(pl.platform);
      for (const st of pl.stores) {
        if (filterIds && pl.platform === activePlatform && !filterIds.has(st.store_id)) continue;
        const ring = storeHull(st);
        if (ring.length < 3) continue;
        L.polygon(ring, {
          color,
          weight: 1.5,
          opacity: 0.85,
          fillColor: color,
          fillOpacity: 0.2,
          interactive: false,
        }).addTo(layer);
      }
    }
  }, [showAreas, map.data, visible, filterIds, activePlatform]);

  // keep Leaflet sized correctly when toggling fullscreen
  useEffect(() => {
    const m = mapRef.current;
    if (m) setTimeout(() => m.invalidateSize(), 60);
  }, [fullscreen]);

  const platformsWithData = new Set((map.data?.platforms ?? []).map((p) => p.platform));

  // tiers present across the currently-visible (and filtered) store markers -> the
  // always-on ring legend, ordered fastest/primary first.
  const presentTiers = useMemo(() => {
    const present = new Set<string>();
    for (const layer of map.data?.platforms ?? []) {
      if (!visible[layer.platform]) continue;
      for (const st of layer.stores) {
        if (filterIds && layer.platform === activePlatform && !filterIds.has(st.store_id))
          continue;
        const t = dominantTier(st.cells as StoreCell[]);
        if (t) present.add(t);
      }
    }
    return [...present].sort((a, b) => tierRank(a) - tierRank(b) || a.localeCompare(b));
  }, [map.data, visible, filterIds, activePlatform]);

  return (
    <div className={`scov ${fullscreen ? "scov--full" : ""}`}>
      <div ref={elRef} className="scov__map" />

      {/* top-right controls: layer toggles + fullscreen */}
      <div className="scov__ctrl">
        {platformKeys().map((p) => (
          <button
            key={p}
            className={`scov__chip ${visible[p] ? "on" : ""}`}
            disabled={!platformsWithData.has(p)}
            onClick={() => setVisible((v) => ({ ...v, [p]: !v[p] }))}
            title={platformsWithData.has(p) ? "Toggle layer" : "No data yet"}
          >
            {/* the registry brand colour, same one the markers + donut slices use */}
            <span className="scov__dot" style={{ background: platformColor(p) }} />
            {platformLabel(p)}
          </button>
        ))}
        <button
          className={`scov__chip ${showAreas ? "on" : ""}`}
          onClick={() => setShowAreas((s) => !s)}
          title="Overlay every visible store's serviceable area"
        >
          <AreaIcon />
          Serviceable areas
        </button>
        <button
          className="scov__icon"
          onClick={() => setFullscreen((f) => !f)}
          title={fullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
          aria-label={fullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
        >
          {fullscreen ? <IconCollapseCorners /> : <IconExpandCorners />}
        </button>
      </div>

      {presentTiers.length > 0 && (
        <div className="scov__legend">
          <span className="scov__legend-title">Store delivery tier</span>
          {presentTiers.map((t) => (
            <span className="scov__legend-item" key={t}>
              <span className="scov__legend-ring" style={{ borderColor: tierColor(t) }} />
              {tierLabel(t)}
            </span>
          ))}
        </div>
      )}

      {selectedCount > 0 && (
        <button className="scov__clear" onClick={clearSelection}>
          Clear selection ({selectedCount})
        </button>
      )}

      {map.isError ? (
        <Overlay>Could not load the map (is ClickHouse configured?).</Overlay>
      ) : map.isLoading ? (
        <div className="scov__overlay scov__overlay--load">
          <span className="spinner" />
          <span>Loading coverage…</span>
        </div>
      ) : (map.data?.platforms.length ?? 0) === 0 ? (
        <Overlay>No coverage yet: run a merchant sweep.</Overlay>
      ) : null}
    </div>
  );
}

function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div className="scov__overlay">
      <span>{children}</span>
    </div>
  );
}

function AreaIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-6 9 6-9 6-9-6z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 15l9 6 9-6" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
    </svg>
  );
}
